import React from "react";
import Header from "../components/layout/header/Header";
import Footer from "../components/layout/footer/Footer";
import "../App.css";
import "../components/layout/privacy and policy/refund.css";
import { Helmet } from "react-helmet-async";

const pageTitle = "Refund & Cancellation Policy | AIGIO";
const pageDescription = "Understand our refund and cancellation policies for AIGIO services.";
type Block =
  | { type: "p"; text: string }
  | { type: "callout"; text: string }
  | { type: "list"; items: string[] }
  | { type: "table"; headers: string[]; rows: string[][] };

type Section = {
  id: string;
  title: string;
  number?: string;
  blocks: Block[];
};

const LAST_UPDATED = "01 July 2026"; // [cite: 62]

const SECTIONS: Section[] = [
  {
    id: "introduction",
    title: "Introduction",
    blocks: [
      {
        type: "p",
        text: `This Refund & Cancellation Policy explains how subscriptions, credits, refunds, and cancellations work for AIGIO, a product of Orglife Private Limited ("we", "us", "our").By subscribing to or paying for any AIGIO service, you agree to the terms below.`, 
      }
    ]
  },
  {
    id: "nature-of-service",
    title: "Nature of our service",
    number: "1", // [cite: 66]
    blocks: [
      {
        type: "p",
        text: `AIGIO is a digital software service delivered electronically through your account. We provide Al-generated brand research, identity, strategy, content, and audits. There are no physical goods and nothing is shipped - delivery is instant inside the application. Please also see our Shipping & Delivery Policy.`, // [cite: 67, 68, 69, 70]
      },
    ],
  },
  {
    id: "subscriptions-credits",
    title: "How subscriptions and credits work",
    number: "2", // [cite: 71]
    blocks: [
      {
        type: "p",
        text: `AIGIO is offered on a paid subscription basis. There is no free plan or free trial.`, // [cite: 72]
      },
      {
        type: "list",
        items: [
          `When you subscribe to a plan, your account is allotted credits. Each feature you use (research, identity, strategy, content, and similar) consumes a set number of credits, shown in the app before you confirm.`, // [cite: 73, 74]
          `Credits are included with your subscription. We do not sell add-on or top-up credits separately.`, // [cite: 75]
          `The Website Audit and Social Audit are available for a minimal one-time charge, limited to once per IP address.`, // [cite: 76]
          `Subscriptions auto-renew at the end of each billing cycle until you cancel. See Section 5.`, // [cite: 77]
        ],
      },
    ],
  },
  {
    id: "non-refundable",
    title: "What is non-refundable",
    number: "3", // [cite: 78]
    blocks: [
      {
        type: "p",
        text: `Because our service is digital and outputs are generated on demand using paid computing resources, the following are final and non-refundable:`, // [cite: 79]
      },
      {
        type: "list",
        items: [
          `Subscription fees for a billing cycle that has already started.`, // [cite: 80]
          `Credits that have already been consumed to generate any output.`, // [cite: 81]
          `Al-generated research, identity, strategy, content, or audits that have already been delivered to your account.`, // [cite: 82]
          `Charges for a Website Audit or Social Audit once the audit has been run.`, // [cite: 83]
          `The unused portion of a billing cycle after cancellation (no pro-rated refunds see Section 5).`, // [cite: 84]
        ],
      },
      {
        type: "p",
        text: `In short: once a billing cycle begins or credits are used, those fees are not refundable. You can cancel any time to stop future renewals, and you keep access until the end of the period you have already paid for.`, // [cite: 85, 86]
      },
    ],
  },
  {
    id: "entitled-refund",
    title: "When you are entitled to a refund",
    number: "4", // [cite: 87]
    blocks: [
      {
        type: "p",
        text: `We will refund you in these specific situations:`, // [cite: 88]
      },
      {
        type: "list",
        items: [
          `Charged but not delivered: money was debited but, due to a technical fault on our side, your subscription, credits, or service were not activated.`, // [cite: 89]
          `Duplicate or double payment: you were charged more than once for the same subscription or purchase the extra charge is refunded in full.`, // [cite: 90]
          `Failed or erroneous auto-renewal: an incorrect or unintended renewal charge caused by a billing error on our side is refunded in full.`, // [cite: 91]
          `Failed transaction: a payment failed at the gateway but money was deducted refunded in full.`, // [cite: 92, 93]
          `Failed generation: if a credit-consuming action fails due to a fault on our side, the credits for that action are returned to your in-app balance you are never charged credits for output you did not receive.`, // [cite: 94]
        ],
      },
    ],
  },
  {
    id: "cancelling-subscription",
    title: "Cancelling your subscription",
    number: "5", // [cite: 95]
    blocks: [
      {
        type: "list",
        items: [
          `You can cancel at any time, either from within your account or by emailing support@aigio.ai. Cancellation stops all future auto-renewals.`, // [cite: 96]
          `You are not charged again after you cancel.`, // [cite: 97]
          `Your access and remaining credits stay active until the end of the billing cycle you have already paid for.`, // [cite: 98]
          `We do not provide pro-rated refunds for the unused part of a billing cycle.`, // [cite: 99]
        ],
      },
    ],
  },
  {
    id: "request-refund",
    title: "How to request a refund",
    number: "6", // [cite: 100]
    blocks: [
      {
        type: "p",
        text: `For any eligible case in Section 4, email support@aigio.ai within 7 days of the charge, including:`, // [cite: 101]
      },
      {
        type: "list",
        items: [
          `The email address on your AIGIO account.`, // [cite: 101]
          `The payment / order ID (from your Razorpay receipt or bank statement).`, // [cite: 102]
          `The date and amount of the charge.`, // [cite: 103]
          `A short description of the issue.`, // [cite: 104]
        ],
      },
    ],
  },
  {
    id: "refund-method-timeline",
    title: "Refund method and timeline",
    number: "7", // [cite: 105]
    blocks: [
      {
        type: "table",
        headers: ["Stage", "Timeline"], // [cite: 106]
        rows: [
          ["We acknowledge your request", "Within 2 business days"], // [cite: 106]
          ["We review and decide", "Within 3 business days of acknowledgement"], // [cite: 106]
          ["Approved refund is initiated", "5 to 7 business days"], // [cite: 106]
          ["Amount reflects in your account", "As per your bank / card issuer, typically 5-10 business days after we initiate"], // [cite: 106]
        ],
      },
      {
        type: "p",
        text: `Approved refunds are always issued to the original payment method. For international payments, the refunded amount may differ slightly from the original due to currency conversion and bank charges outside our control.`, // [cite: 107, 108]
      },
    ],
  },
  {
    id: "chargebacks",
    title: "Chargebacks",
    number: "8", // [cite: 109]
    blocks: [
      {
        type: "p",
        text: `If you believe a charge is incorrect, please contact us first -- we resolve genuine issues quickly. Raising a chargeback without contacting us may lead to suspension of your account pending resolution.`, // [cite: 110, 111]
      },
    ],
  },
  {
    id: "discretion-changes",
    title: "Discretion and changes",
    number: "9", // [cite: 112]
    blocks: [
      {
        type: "p",
        text: `Any refund granted outside this policy is at our sole discretion, decided case by case, and does not waive this policy for future requests.`, // [cite: 113]
      },
      {
        type: "p",
        text: `We may update this policy from time to time; the "Last updated" date above reflects the current version.`, // [cite: 114]
      },
    ],
  },
  {
    id: "contact",
    title: "Contact",
    number: "10", // [cite: 115]
    blocks: [
      {
        type: "p",
        text: `Questions about refunds or cancellations? Email support@aigio.ai or visit our Contact Us page.`, // [cite: 116]
      },
    ],
  },
];

function renderBlock(block: Block, key: number) {
  switch (block.type) {
    case "callout":
      return (
        <div key={key} className="legal-callout">
          "{block.text}"
        </div>
      );
    case "list":
      return (
        <ul
          key={key}
          className="legal-list"
          style={{ listStyleType: "disc", paddingLeft: "24px", margin: "14px 0" }}
        >
          {block.items.map((item, i) => (
            <li key={i} style={{ marginBottom: "10px", display: "list-item" }}>
              {item}
            </li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div key={key} style={{ overflowX: "auto", margin: "18px 0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #333" }}>
                {block.headers.map((h, i) => (
                  <th key={i} style={{ padding: "10px", fontWeight: "bold" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #222" }}>
                  {row.map((cell, j) => (
                    <td key={j} style={{ padding: "10px" }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return (
        <p key={key} className="legal-paragraph">
          {block.text}
        </p>
      );
  }
}

const RefundPolicy: React.FC = () => {
  const [activeId, setActiveId] = React.useState(SECTIONS[0].id);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -65% 0px", threshold: 0 },
    );

    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <div className="page-shell page-shell--legal">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <Header />
   

        <main className="refund-page">
          <div className="legal-page__inner">
            <p className="legal-page__kicker">LEGAL DOCUMENTATION</p>
            <h1 className="legal-page__title">Refund & Cancellation Policy</h1>
            <p className="legal-page__updated">
              Last Updated: {LAST_UPDATED}. AIGIO is a product of Orglife Private Limited
            </p>

            <div className="legal-page__body">
              <aside className="legal-toc" aria-label="Table of contents">
                <ul className="legal-toc__list">
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={`legal-toc__link ${
                          activeId === s.id ? "legal-toc__link--active" : ""
                        }`}
                      >
                        {s.number ? `${s.number}. ` : ""}{s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </aside>

              <div className="legal-content">
                {SECTIONS.map((s) => (
                  <section key={s.id} id={s.id} className="legal-section">
                    <h2 className="legal-section__title">
                      {s.number ? `${s.number}. ` : ""}{s.title}
                    </h2>
                    {s.blocks.map((b, j) => renderBlock(b, j))}
                  </section>
                ))}

                <p className="legal-page__contact">
                  If you have any questions about refunds or cancellations, please contact our legal department.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default RefundPolicy;